import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonColors, atomButtonDesigns, atomButtonShapes} from '@s-ui/react-atom-button'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import IconHeartOutline from '@adv-mt/mt-svg-icons/lib/HeartOutline'
import IconHeartSolid from '@adv-mt/mt-svg-icons/lib/HeartSolid'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

const useAddFavorite = ({contractId, hitInformation2}) => {
  const [isLoginFlowCancelled, setIsLoginFlowCancelled] = useState(false)
  const [isAddedToFavorites, setIsAddedToFavorites] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)

  const {domain} = useContext(Context)
  const {triggerLoginModal} = useContext(LoginModalContext)

  const config = domain.get('config')
  const {LOGIN_SOURCE_ID_DEALER_FAV} = config.get('login')
  const {HIT_ORIGINS} = config.get('tracking')

  useEffect(() => {
    showLoginModal &&
      triggerLoginModal({
        modalProps: {
          source: LOGIN_SOURCE_ID_DEALER_FAV,
          trackingOrigin: HIT_ORIGINS.DEALER_FAVORITED,
          hitInformation2,
          onLogin: () => {
            domain
              .get('add_user_favorites_dealer_use_case')
              .execute({contractId})
              .then(([, response]) => {
                response && setIsAddedToFavorites(true)
              })
          },
          onClose: () => {
            setIsLoginFlowCancelled(true)
            setShowLoginModal(false)
          }
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLoginModal, contractId])

  return {isAddedToFavorites, isLoginFlowCancelled, setShowLoginModal, showLoginModal}
}

const FavoriteButton = ({
  dealer,
  isFavorite = false,
  label,
  size,
  iconSize = atomIconSizes.medium,
  isFitted = false,
  className = '',
  trackingExtraData,
  authenticationHitInformation2
}) => {
  const {i18n, domain} = useContext(Context)
  const {track} = useAnalyticsTrack()

  const [isMarkedFavorite, setIsMarkedFavorite] = useState(isFavorite)
  const {isAddedToFavorites, isLoginFlowCancelled, setShowLoginModal} = useAddFavorite({
    contractId: dealer?.id,
    hitInformation2: authenticationHitInformation2
  })

  const config = domain.get('config')
  const {USER_NOT_LOGGED_IN_USER_ERROR} = config.get('ERRORS')
  const {
    TRACKING_EVENT_NAMES: {DEALER_UNFAVORITED, DEALER_FAVORITED}
  } = config.get('tracking')

  useEffect(() => {
    if (isAddedToFavorites) {
      setIsMarkedFavorite(true)
      trackFavorite()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddedToFavorites, isLoginFlowCancelled])

  const trackFavorite = () => {
    track({
      event: isMarkedFavorite ? DEALER_UNFAVORITED : DEALER_FAVORITED,
      data: {
        ...trackingExtraData,
        seller_type: trackingExtraData.seller_type || 'profesional',
        products: [{id: `dealer${dealer?.id}`, category: 'dealer'}]
      }
    })
  }

  const handleFavoriteDealer = async () => {
    const handleFavUseCase = isMarkedFavorite
      ? 'delete_user_favorites_dealer_use_case'
      : 'add_user_favorites_dealer_use_case'

    const [error] = await domain.get(handleFavUseCase).execute({contractId: dealer.id})

    if (error instanceof USER_NOT_LOGGED_IN_USER_ERROR) return setShowLoginModal(true)

    if (!error) {
      trackFavorite()
      setIsMarkedFavorite(!isMarkedFavorite)
    }
  }

  return (
    <AtomButton
      onClick={handleFavoriteDealer}
      leftIcon={
        <div className="mt-HeroDealer-favoriteIcon">
          {isMarkedFavorite ? <IconHeartSolid size={iconSize} /> : <IconHeartOutline size={iconSize} />}
        </div>
      }
      design={atomButtonDesigns.SOLID}
      color={atomButtonColors.PRIMARY}
      shape={atomButtonShapes.CIRCULAR}
      negative
      fullWidth={false}
      isFitted={isFitted}
      size={size}
      className={className}
      aria-pressed={isMarkedFavorite}
      aria-label={!label ? `${i18n.t(isMarkedFavorite ? 'FAVORITES.REMOVE' : 'FAVORITES.ADD')}` : null}
    >
      {label}
    </AtomButton>
  )
}

FavoriteButton.propTypes = {
  dealer: PropTypes.object.isRequired,
  isFavorite: PropTypes.bool,
  label: PropTypes.string,
  size: PropTypes.string,
  iconSize: PropTypes.string,
  isFitted: PropTypes.bool,
  className: PropTypes.string,
  trackingExtraData: PropTypes.object,
  authenticationHitInformation2: PropTypes.string
}

export default FavoriteButton
